/**
 * How far a batch of documents actually is through the pipeline (LAT-870).
 *
 * The first version of the processing banner divided settled documents by total documents,
 * which made the bar a step function: a single-file upload sat at 0% for the whole parse and
 * then jumped to 100%. The pipeline has visible intermediate beats — the status column is
 * already walking Queued → Parsing → Summarizing → Ready — so the bar can move with them.
 *
 * Each document contributes a fraction of `1 / N`, and the fraction is the share of *time*
 * already elapsed when it reached its current beat:
 *
 * | Status                    | Contribution                       |
 * |---------------------------|------------------------------------|
 * | `queued`, `not_processed` | 0                                  |
 * | `parsing`                 | `DOCUMENT_PROGRESS_WEIGHTS.started` |
 * | `summarizing`             | `DOCUMENT_PROGRESS_WEIGHTS.parsed`  |
 * | `ready`, `failed`         | 1                                  |
 * | `waiting_for_input`       | 1                                  |
 * | `cancelled`               | 1                                  |
 *
 * ## Failed documents contribute 1, on purpose
 *
 * A failed document could instead be frozen at the boundary it died on (`…weights.started`
 * for a parse failure), which reads as "it never finished". It is not what this bar measures.
 * The bar answers "how much of the wait is over" — it exists to tell someone whether they
 * can stop watching — and a failed document is done waiting: nothing will move it again
 * without a Retry, so the banner it belongs to should be allowed to reach 100% and stand
 * down. Freezing it short would leave a bar permanently stuck at 60% with nothing running,
 * which is the more dishonest of the two readings. Success is reported separately and by
 * name: the counts line splits Done out from In progress, and the batch-ready bar that
 * replaces this banner says "M of N failed" in destructive red.
 *
 * ## Skipped beats are normal, and monotonic anyway
 *
 * 19 % of production runs reuse a cached job (`metricsJson.reusedFromJobId`) and finish
 * without ever starting, so a re-uploaded file can go straight from `queued` to `ready`.
 * Nothing here interpolates or remembers: each contribution is a pure function of the status
 * the document is in *now*, and the boundaries increase along the pipeline order, so a
 * document that skips beats jumps forward and never backwards.
 *
 * ## Where the weights come from
 *
 * See `DOCUMENT_PROGRESS_WEIGHTS` below. They are measured, not guessed, and re-calibrating
 * them is editing two numbers.
 */

import type { DocumentStatusKind } from "./document-row-view";

/**
 * The two fractional boundaries. Round numbers on purpose — the bar communicates *relative*
 * stage cost, not a stopwatch — but the relative order is measured.
 *
 * Measured 2026-08-17 from complete read-only exports of `artifactProcessingJobs` on prod
 * `rapid-kookabura-976` (55 fully-timed runs, 2026-08-11 → 08-17) and UAT
 * `determined-grasshopper-528` (198), cross-checked against 30 days of Axiom `df-convex`
 * `artifactProcessing.job_succeeded` (identical prod medians to the millisecond). Medians per
 * beat: uncontended workpool pickup 0.8 s → `parse_document` 2.2 s pooled but 14.2 s for
 * single-file uploads (parse tracks file size) → `describe_figures` 0.13 s +
 * `generate_document_card` 6.4 s. Parse is therefore the visibly longer stage for real
 * documents, and each boundary keeps a ~0.1 minimum increment so crossing any of them
 * visibly moves the bar even where the stage behind it is near-instant.
 *
 * Two things a re-calibrator should not undo. `describe_figures` belongs to the
 * *summarizing* beat, not one of its own: `enqueueArtifactJobs` writes all three stage jobs
 * up front, so the card job is already in flight while figures run and
 * `resolveDocumentDisplayStatus` says `summarizing` — and figures has described zero figures
 * in 30 days in every environment anyway. And the *pooled* 8.9 s pre-parse wait is not this
 * document's: the Docling workpool is `maxParallelism: 1`, so document k of a batch waits
 * ≈ 0.8 s + (k−1) × (parse + card), and that serialization is already priced as the other
 * documents' zero contribution. Charging it here too would make a lone document claim 60 %
 * the instant parsing began.
 *
 * Re-calibrating is editing the two numbers below; nothing else encodes them.
 */
export const DOCUMENT_PROGRESS_WEIGHTS = {
  /** Reached on `queued` → `parsing`: the pickup, plus the minimum visible increment. */
  started: 0.1,
  /** Reached on `parsing` → `summarizing`, i.e. the parse landed — the long stage. */
  parsed: 0.6,
} as const;

/**
 * A batch member the pipeline may still move.
 *
 * `not_processed` counts as unsettled on purpose, and it is the subtle one: `createFile`
 * schedules the enqueue rather than writing the job inline, so a file that was uploaded a
 * moment ago legitimately reads "nothing has queued this" for one tick. Treating that as
 * settled flashed "4 documents ready" before parsing had started.
 *
 * A row that has not arrived in the query yet (`undefined`) is unsettled for the same
 * reason: the id exists because the backend accepted the file, so the row is on its way.
 *
 * `cancelled` is the counterpart, and the reason the backend gives a stop its own word: a
 * stopped file and a file nothing has queued are the same picture, so while a stop reported as
 * `not_processed` the banner over it could never stand down — the person switched the pipeline
 * off themselves and the screen went on saying "Claudia is reading 1 document" above it.
 */
export function isSettledDocumentStatus(status: DocumentStatusKind | undefined): boolean {
  // `waiting_for_input` is settled *for the bar*, which measures how much of the wait is over.
  // Nothing will move a parked document until its person answers, so holding the banner open
  // for it would leave a progress bar running over a pipeline that has stopped — and the
  // answer is on the row, not in the banner. A stopped document is settled for the stronger
  // reason: nothing is waiting on anybody, and only a Try again will move it.
  return (
    status === "ready" ||
    status === "failed" ||
    status === "waiting_for_input" ||
    status === "cancelled"
  );
}

/** The pipeline is working on this file right now — the rows that earn a live spinner. */
export function isActiveDocumentStatus(status: DocumentStatusKind | undefined): boolean {
  return status === "parsing" || status === "summarizing";
}

/** This document's share of `1 / N`, by the beat it has reached. */
export function documentProgressFraction(status: DocumentStatusKind | undefined): number {
  if (isSettledDocumentStatus(status)) return 1;
  if (status === "summarizing") return DOCUMENT_PROGRESS_WEIGHTS.parsed;
  if (status === "parsing") return DOCUMENT_PROGRESS_WEIGHTS.started;
  // `queued`, `not_processed`, and the row that has not arrived yet: nothing has started.
  return 0;
}

export type DocumentProcessingProgress = {
  totalCount: number;
  /** Nothing has started on these: `queued`, `not_processed`, and rows still in flight. */
  queuedCount: number;
  /** `parsing` + `summarizing` — the pipeline is mid-document. */
  inProgressCount: number;
  /** Settled either way, which is what the banner's "Done" means. */
  doneCount: number;
  /** 0–1. Stage-weighted, so a single document climbs rather than jumping. */
  fraction: number;
  /** The same number as a rounded percentage, for the progress bar and its label. */
  percent: number;
};

/**
 * The batch's progress from the statuses of its members.
 *
 * `undefined` entries are batch members whose row the query has not returned yet; they are
 * counted (the file exists) and contribute nothing, which is the honest reading.
 */
export function resolveProcessingProgress(
  statuses: readonly (DocumentStatusKind | undefined)[]
): DocumentProcessingProgress {
  const totalCount = statuses.length;
  let inProgressCount = 0;
  let doneCount = 0;
  let weighted = 0;

  for (const status of statuses) {
    weighted += documentProgressFraction(status);
    if (isSettledDocumentStatus(status)) doneCount += 1;
    else if (isActiveDocumentStatus(status)) inProgressCount += 1;
  }

  const fraction = totalCount === 0 ? 0 : weighted / totalCount;
  return {
    totalCount,
    queuedCount: totalCount - inProgressCount - doneCount,
    inProgressCount,
    doneCount,
    fraction,
    percent: Math.round(fraction * 100),
  };
}
